import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, money, API_BASE } from "@/lib/api";
import { TrendingUp, AlertTriangle, Receipt, Wallet, ArrowRight, BarChart3, Download } from "lucide-react";
import HelpTip from "@/components/HelpTip";
import { SECTION_HELP } from "@/lib/helpTexts";

const BUCKET_LABELS = {
    vigente: "Vigente",
    "1_30": "1-30 días",
    "31_60": "31-60 días",
    "61_90": "61-90 días",
    "90_plus": "90+ días",
};

const BUCKET_COLORS = {
    vigente: "bg-emerald-500",
    "1_30": "bg-amber-400",
    "31_60": "bg-orange-500",
    "61_90": "bg-red-500",
    "90_plus": "bg-red-700",
};

const BUCKETS = ["vigente", "1_30", "31_60", "61_90", "90_plus"];

function BucketBar({ buckets, total, testId }) {
    if (!total) return <div className="h-2 bg-slate-100 rounded-sm" />;
    return (
        <div className="flex h-2 rounded-sm overflow-hidden bg-slate-100" data-testid={testId}>
            {BUCKETS.map((b) => {
                const v = buckets[b] || 0;
                const pct = (v / total) * 100;
                if (pct < 0.5) return null;
                return <div key={b} className={BUCKET_COLORS[b]} style={{ width: `${pct}%` }} title={`${BUCKET_LABELS[b]}: ${money(v)}`} />;
            })}
        </div>
    );
}

function KPI({ label, value, danger, accent, testId }) {
    return (
        <div className={`fm-card p-4 ${danger ? "border-l-4 border-l-red-600" : accent ? "border-l-4 border-l-emerald-500" : ""}`} data-testid={testId}>
            <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">{label}</div>
            <div className={`font-mono font-bold text-2xl mt-1 ${danger ? "text-red-600" : ""}`}>{money(value)}</div>
        </div>
    );
}

export default function Aging() {
    const [data, setData] = useState(null);
    const [tab, setTab] = useState("ar"); // ar | ap

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/finance/aging");
                setData(data);
            } catch (e) {
                console.warn(e);
            }
        })();
    }, []);

    if (!data) {
        return <div className="text-sm text-slate-500 font-mono">Cargando reporte de antigüedad...</div>;
    }

    const active = tab === "ar" ? data.ar : data.ap;
    const overdueTotal = BUCKETS.filter((b) => b !== "vigente").reduce((s, b) => s + (active.buckets[b] || 0), 0);
    const list = tab === "ar" ? active.by_client : active.by_provider;
    const itemKey = tab === "ar" ? "client_id" : "provider_name";
    const itemLabel = tab === "ar" ? "client_name" : "provider_name";
    const itemCountKey = tab === "ar" ? "invoice_count" : "payable_count";

    return (
        <div className="space-y-4" data-testid="aging-page">
            {/* Header */}
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <h2 className="font-display font-black text-2xl tracking-tight flex items-center gap-2">Antigüedad de Saldos<HelpTip text={SECTION_HELP.aging_report} variant="?" /></h2>
                    <div className="text-xs text-slate-500 font-mono">Análisis aging · Corte: {data.today}</div>
                </div>
                <div className="flex items-center gap-2">
                    <a
                        href={`${API_BASE}/pdf/aging/${tab}`}
                        target="_blank"
                        rel="noreferrer"
                        className="fm-btn-primary text-xs"
                        data-testid={`download-aging-${tab}-pdf`}
                        title="Descarga el reporte de antigüedad en PDF"
                    >
                        <Download className="w-3.5 h-3.5" /> PDF
                    </a>
                    <Link to="/receivable" className="fm-btn-outline text-xs"><Receipt className="w-3.5 h-3.5" /> CxC</Link>
                    <Link to="/payable" className="fm-btn-outline text-xs"><Wallet className="w-3.5 h-3.5" /> CxP</Link>
                </div>
            </div>

            {/* Tabs */}
            <div className="fm-card p-1 inline-flex items-center gap-1" data-testid="aging-tabs">
                <button
                    type="button"
                    onClick={() => setTab("ar")}
                    data-testid="aging-tab-ar"
                    className={`px-4 py-2 text-xs font-bold uppercase tracking-wider rounded-sm flex items-center gap-2 transition-colors ${tab === "ar" ? "bg-slate-900 text-white" : "text-slate-500 hover:bg-slate-100"}`}
                >
                    <Receipt className="w-3.5 h-3.5" /> Por cobrar (AR)
                </button>
                <button
                    type="button"
                    onClick={() => setTab("ap")}
                    data-testid="aging-tab-ap"
                    className={`px-4 py-2 text-xs font-bold uppercase tracking-wider rounded-sm flex items-center gap-2 transition-colors ${tab === "ap" ? "bg-slate-900 text-white" : "text-slate-500 hover:bg-slate-100"}`}
                >
                    <Wallet className="w-3.5 h-3.5" /> Por pagar (AP)
                </button>
            </div>

            {/* Bucket KPIs */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="aging-kpis">
                {BUCKETS.map((b) => (
                    <KPI
                        key={b}
                        label={BUCKET_LABELS[b]}
                        value={active.buckets[b] || 0}
                        danger={b === "61_90" || b === "90_plus"}
                        accent={b === "vigente"}
                        testId={`aging-bucket-${b}`}
                    />
                ))}
            </div>

            {/* Summary card */}
            <div className="fm-card p-5">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                    <div>
                        <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Total {tab === "ar" ? "por cobrar" : "por pagar"}</div>
                        <div className="font-mono font-bold text-3xl">{money(active.total)}</div>
                    </div>
                    <div>
                        <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Vencido</div>
                        <div className="font-mono font-bold text-3xl text-red-600">{money(overdueTotal)}</div>
                    </div>
                    <div>
                        <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">% Vencido</div>
                        <div className="font-mono font-bold text-3xl">{active.total > 0 ? Math.round((overdueTotal / active.total) * 100) : 0}%</div>
                    </div>
                </div>
                <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-2">Distribución por bucket</div>
                <BucketBar buckets={active.buckets} total={active.total} testId="aging-summary-bar" />
                <div className="flex items-center gap-3 mt-3 text-[10px] uppercase tracking-widest font-bold text-slate-500 flex-wrap">
                    {BUCKETS.map((b) => (
                        <div key={b} className="inline-flex items-center gap-1.5">
                            <span className={`w-2 h-2 ${BUCKET_COLORS[b]} rounded-sm`} />
                            {BUCKET_LABELS[b]}
                        </div>
                    ))}
                </div>
            </div>

            {/* Top deudores / acreedores */}
            <div className="fm-card overflow-hidden">
                <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
                    <h3 className="font-display font-bold text-base">
                        {tab === "ar" ? "Top clientes con saldo" : "Top proveedores por pagar"}
                    </h3>
                    <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">
                        {list.length} {tab === "ar" ? "cliente(s)" : "proveedor(es)"}
                    </div>
                </div>
                {list.length === 0 ? (
                    <div className="p-10 text-center text-sm text-slate-500">
                        <BarChart3 className="w-10 h-10 mx-auto mb-2 text-slate-300" strokeWidth={1} />
                        Sin saldos pendientes.
                    </div>
                ) : (
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="bg-slate-50 border-b border-slate-200">
                                {[tab === "ar" ? "Cliente" : "Proveedor", "Docs", "Saldo", "Vencido", "Distribución", "Max días", ""].map((h) => (
                                    <th key={h} className="text-left py-2.5 px-3 text-[10px] font-bold text-slate-500 uppercase tracking-wider">{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {list.map((row, i) => {
                                const overdue = BUCKETS.filter((b) => b !== "vigente").reduce((s, b) => s + (row.buckets[b] || 0), 0);
                                return (
                                    <tr key={row[itemKey] + i} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`aging-row-${i}`}>
                                        <td className="py-3 px-3 font-medium">{row[itemLabel] || "—"}</td>
                                        <td className="py-3 px-3 text-center font-mono">{row[itemCountKey]}</td>
                                        <td className="py-3 px-3 text-right font-mono font-bold">{money(row.total_balance)}</td>
                                        <td className={`py-3 px-3 text-right font-mono ${overdue > 0 ? "text-red-600 font-bold" : "text-slate-400"}`}>{money(overdue)}</td>
                                        <td className="py-3 px-3 w-64"><BucketBar buckets={row.buckets} total={row.total_balance} /></td>
                                        <td className="py-3 px-3 text-center">
                                            {row.max_days_overdue > 0
                                                ? <span className="fm-badge fm-badge-critical">{row.max_days_overdue}d</span>
                                                : <span className="fm-badge fm-badge-positive">Vigente</span>}
                                        </td>
                                        <td className="py-3 px-3 text-right">
                                            {tab === "ar" && row.client_id && row.client_id !== "particular" && (
                                                <Link
                                                    to={`/statement/${row.client_id}`}
                                                    data-testid={`statement-link-${i}`}
                                                    className="text-xs text-slate-700 hover:text-red-600 inline-flex items-center gap-1 font-bold uppercase tracking-wider"
                                                >
                                                    Estado de cuenta <ArrowRight className="w-3 h-3" />
                                                </Link>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Critical alert */}
            {overdueTotal > 0 && (overdueTotal / active.total) > 0.3 && (
                <div className="fm-card p-4 border-l-4 border-l-red-600 bg-red-50 flex items-start gap-3" data-testid="aging-critical-alert">
                    <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <div>
                        <div className="font-display font-bold text-sm text-red-900">Alerta de flujo</div>
                        <div className="text-xs text-slate-700 mt-1">
                            Más del 30% del saldo está vencido ({Math.round((overdueTotal / active.total) * 100)}%).
                            {tab === "ar"
                                ? " Priorizar gestión de cobranza con los clientes en buckets 61-90 y 90+."
                                : " Riesgo de relación con proveedores: priorizar pagos vencidos."}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
